import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, List, ListItem, ListItemText, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { LeaveSummary, MonthlyLeavePoint, LeaveTypeUsage } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import HBars from '../charts/HBars';
import StackedColumns from '../charts/StackedColumns';
import StatTile from '../charts/StatTile';

const ADMIN_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager];

const LeaveWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<LeaveSummary | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getLeaveSummary(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label={t('widgets.leave.pending')} value={d.pending} tone={d.pending > 0 ? 'warning' : 'default'} />
                <StatTile label={t('widgets.leave.approved')} value={d.approved} tone="good" />
                <StatTile label={t('widgets.leave.rejected')} value={d.rejected} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t('widgets.leave.allToDate')}</Typography>
            {d.byType.length > 0 && (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('widgets.leave.pendingByType')}</Typography>
                    <HBars data={d.byType.map(bt => ({ label: bt.leaveType, value: bt.count }))} colorIndex={3} />
                </>
            )}
            <List dense>
                {d.pendingQueue.slice(0, 5).map(r => (
                    <ListItem key={r.id} secondaryAction={r.daysOverdue > 0 ? <Chip size="small" color="error" label={t('widgets.leave.overdue', { days: r.daysOverdue })} /> : null}>
                        <ListItemText primary={`${r.applicantName} — ${r.leaveType}`} secondary={t('widgets.leave.fromDate', { date: r.startDate })} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

const loadLeaveTrend = (institutionId: string): Promise<MonthlyLeavePoint[]> =>
    DashboardService.getLeaveTrend(institutionId);

const LeaveTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadLeaveTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The backend zero-fills months, so an empty array means no window at all — an honest empty,
    // while a window of all-zero months is a real (quiet) chart, not an absence of data.
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t('widgets.leave.noApplications')}
            </Typography>
        );
    }
    return (
        <StackedColumns
            data={d.map(p => ({ label: p.month, segments: [p.pending, p.approved, p.rejected, p.cancelled] }))}
            seriesLabels={[t('widgets.leave.pending'), t('widgets.leave.approved'), t('widgets.leave.rejected'), t('widgets.leave.cancelled')]}
        />
    );
};

const loadLeaveByType = (institutionId: string): Promise<LeaveTypeUsage[]> =>
    DashboardService.getLeaveByType(institutionId);

const LeaveByTypeWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadLeaveByType, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t('widgets.leave.noApproved')}
            </Typography>
        );
    }
    return (
        <Box>
            <HBars
                data={d.map(u => ({
                    label: u.type,
                    value: u.applications,
                    tooltip: [
                        { label: t('widgets.leave.applications'), value: String(u.applications) },
                        { label: t('widgets.leave.days'), value: String(u.days) },
                    ],
                }))}
                colorIndex={3}
            />
            {/* The window is the whole calendar year, not year-to-date: leave approved now for a
                future date is already counted. The caption says so rather than reading as
                consumed-so-far. */}
            <Typography variant="caption" color="text.secondary">
                {t('widgets.leave.yearCaption')}
            </Typography>
        </Box>
    );
};

export const leaveWidgets: WidgetDef[] = [
    { id: 'leave.summary', module: 'leave', titleKey: 'widgets.titles.leaveSummary', subtitleKey: 'widgets.subtitles.leaveSummary', roles: ADMIN_ROLES, size: 'md', component: LeaveWidget },
    { id: 'leave.trend', module: 'leave', titleKey: 'widgets.titles.leaveTrend', subtitleKey: 'widgets.subtitles.leaveTrend', roles: ADMIN_ROLES, size: 'lg', component: LeaveTrendWidget },
    { id: 'leave.byType', module: 'leave', titleKey: 'widgets.titles.leaveByType', subtitleKey: 'widgets.subtitles.leaveByType', roles: ADMIN_ROLES, size: 'md', component: LeaveByTypeWidget },
];
